import axios from 'axios';
import { reduxStore } from '../store/reduxStore';
import { logout } from '../store/authSlice';
import { toast } from 'react-toastify';
import auth from './auth';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // Enable cookie-based authentication
});

// Add request interceptor to log outgoing requests
api.interceptors.request.use(
  (config) => {
    const token = auth.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log subscription billing requests specifically
    if (config.url?.includes('/subscriptions/billing/start')) {
      console.log('API Request - URL:', config.url);
      console.log('API Request - Method:', config.method);
      console.log('API Request - Data:', config.data);
      console.log('API Request - Data JSON:', JSON.stringify(config.data, null, 2));
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're in the middle of a logout
let isLoggingOut = false;

// Login/register attempts — 401 is expected; don't toast or clear session globally
const isPublicAuthRequest = (url = '') =>
  ['/auth/login', '/auth/google', '/auth/forgot', '/auth/reset', '/users/register'].some(
    (path) => url.includes(path)
  );

api.interceptors.response.use(
  // On success, just return the response.
  (response) => response,

  // On error:
  (error) => {
    // If there's no response at all, it's a network or CORS issue, etc.
    if (!error.response) {
      if (!isLoggingOut) {
        toast.error('Network Error. Please try again later.');
      }
      return Promise.reject(error);
    }

    // 1) Real HTTP status from Axios
    const { status } = error.response;

    // 2) ApiError fields (if your server sends them)
    const { message } = error.response.data || {};

    const skipGlobalAuthHandling = isPublicAuthRequest(error.config?.url);

    switch (status) {
      case 400:
        if (!isLoggingOut && !skipGlobalAuthHandling) {
          toast.error(message || 'Bad Request');
        }
        break;

      case 401:
        if (!isLoggingOut && !skipGlobalAuthHandling) {
          toast.error(message || 'Unauthorized, please log in');
          // Set logout flag to prevent multiple toasts
          isLoggingOut = true;
          // Dispatch logout action
          reduxStore.dispatch(logout());
        }
        break;

      case 403:
        if (!isLoggingOut && !skipGlobalAuthHandling) {
          toast.error("You don't have permission to access this resource");
          // Set logout flag to prevent multiple toasts
          isLoggingOut = true;
          // Dispatch logout action
          reduxStore.dispatch(logout());
        }
        break;

      case 404:
        // Don't show toast for subscription 404s - they're expected
        // Only show toast for other 404s that might be real errors
        if (!isLoggingOut && !error.config?.url?.includes('/subscriptions/')) {
          toast.error(message || 'Resource not found');
        }
        break;

      case 422:
        if (!isLoggingOut) {
          toast.error(message || 'Validation Error');
        }
        break;

      case 409:
        if (!isLoggingOut) {
          toast.error(message || 'Duplicate request, please check your email');
        }
        break;

      default:
        if (!isLoggingOut) {
          toast.error(message || 'An error occurred. Please try again.');
        }
        break;
    }
    return Promise.reject(error);
  }
);

// Export a function to reset the logout flag
export const resetLogoutFlag = () => {
  isLoggingOut = false;
};

export default api;
